import clsx from "clsx";
import type { ButtonHTMLAttributes } from "react";
import { POSITION_NAMES, type Position } from "../../lib/positions";

/**
 * Chips de posición, de estado y de filtro — secciones 8.3 y 8.4.
 *
 * EL COLOR NUNCA VA SOLO
 *   El chip de posición lleva la etiqueta corta escrita (POR/DEF/MED/DEL) y el de estado lleva
 *   glifo **y** texto (*Lesionado*, *Duda*, *Sancionado*). Quien no distinga el ámbar del rojo
 *   sigue leyendo lo mismo que los demás.
 */

/* ───────────────────────────────────────────────────────── posición ── */

/**
 * Fondo con el token de la posición y texto en `paper`: sobre el ámbar o el verde claro el texto
 * `ink` no llegaría a 4,5:1, mientras que `paper` se queda entre 6,4:1 y 11:1 en los cuatro.
 */
const POSITION_CLASSES: Record<Position, string> = {
  POR: "bg-pos-POR text-paper",
  DEF: "bg-pos-DEF text-paper",
  MED: "bg-pos-MED text-paper",
  DEL: "bg-pos-DEL text-paper",
};

export interface PositionChipProps {
  position: Position;
  className?: string;
}

export function PositionChip({ position, className }: PositionChipProps) {
  return (
    <span
      className={clsx(
        "inline-flex h-[19px] shrink-0 items-center rounded-full px-[7px]",
        "text-[10px] font-bold tracking-wider",
        POSITION_CLASSES[position],
        className,
      )}
      title={POSITION_NAMES[position]}
    >
      {position}
    </span>
  );
}

/* ─────────────────────────────────────────────────────────── estado ── */

export type Status = "ok" | "doubt" | "injured" | "sanctioned" | "unknown";

/** Texto corto de cada estado (sección 8.3). `ok` y `unknown` no se pintan: no hay nada que avisar. */
export const STATUS_LABELS: Record<Status, string | null> = {
  ok: null,
  doubt: "Duda",
  injured: "Lesionado",
  sanctioned: "Sancionado",
  unknown: null,
};

/** Glifo tipográfico —no un emoji— para que el estado no dependa solo del color. */
const STATUS_GLYPHS: Record<Status, string> = {
  ok: "",
  doubt: "●",
  injured: "✚",
  sanctioned: "■",
  unknown: "",
};

const STATUS_CLASSES: Record<Status, string> = {
  ok: "",
  doubt: "text-money border-money/40 bg-money/10",
  injured: "text-down border-down/40 bg-down/10",
  sanctioned: "text-ink border-border bg-white/5",
  unknown: "",
};

export interface StatusChipProps {
  status: Status;
  /** `statusText` de la sección 9.2: lo que dice Biwenger, si lo dice. */
  detail?: string | null;
  className?: string;
}

/** Devuelve `null` cuando el jugador está disponible: un chip «Todo bien» sería ruido. */
export function StatusChip({ status, detail, className }: StatusChipProps) {
  const label = STATUS_LABELS[status];
  if (label === null) return null;

  return (
    <span
      className={clsx(
        "inline-flex h-5 shrink-0 items-center gap-1 rounded-full border px-[7px] text-[11px]",
        STATUS_CLASSES[status],
        className,
      )}
      title={detail ?? undefined}
    >
      <span aria-hidden="true">{STATUS_GLYPHS[status]}</span>
      {label}
    </span>
  );
}

/* ─────────────────────────────────────────────────────────── filtro ── */

export interface ToggleChipProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "aria-pressed"
> {
  pressed: boolean;
}

/**
 * Chip pulsable de las barras de filtro y de los selectores de formación.
 * Mide 44 px de alto como cualquier otro objetivo táctil (sección 8.1).
 */
export function ToggleChip({
  pressed,
  className,
  type,
  ...rest
}: ToggleChipProps) {
  return (
    <button
      type={type ?? "button"}
      aria-pressed={pressed}
      className={clsx(
        "inline-flex min-h-touch shrink-0 items-center rounded-full border px-3 text-[13px]",
        "transition-colors disabled:cursor-not-allowed disabled:opacity-45",
        pressed
          ? "border-pitch bg-pitch font-semibold text-paper"
          : "border-border bg-surface text-ink hover:border-muted",
        className,
      )}
      {...rest}
    />
  );
}
